/**
 * Dynamic field rendering: resolver + specialized renderers.
 *
 * Instead of one large render function with a huge context, we use:
 * 1. getDynamicFieldRendererDescriptor(attr, input) – returns which renderer to use and minimal static params.
 * 2. Specialized renderer components – each receives a small, focused props interface.
 *
 * readOnly drives view-only behavior. External deps are passed per-renderer via runtime/supplies.
 */

import React from 'react';
import { Alert, Box, Text, TextInput, Tooltip, Select, Group, Badge, ActionIcon, Button, Anchor, Loader } from '@mantine/core';
import { IconInfoCircle, IconX } from '@tabler/icons-react';
import { GenericReferenceField } from '../common/GenericReferenceField';
import { MultiReferenceEntityBadge } from '../common/MultiReferenceEntityBadge';
import { createEntityRef, type EntityRef, tryCreateEntityRef } from '../../types';
import { isARef } from '../../utils/entityPropertyUtils';
import { debugLog } from '../../utils/debugLog';
import { getEntityTypeFlags } from '../../utils/entityTypeDetection';
import { formatPhoneDisplayIfApplicable } from '../../utils/phoneUtils';

// ---------------------------------------------------------------------------
// Resolver input – minimal data to decide renderer type (no form values/callbacks)
// ---------------------------------------------------------------------------

/** Input to getDynamicFieldRendererDescriptor. 6 properties. */
export interface GetDynamicFieldRendererDescriptorInput {
    /** Schema attribute for the field. */
    attr: DynamicFieldAttribute;
    /** Entity type (e.g. User, Group, SetTransition, PolicyRule). */
    entityType: string | null | undefined;
    /** Normalize attribute name to form key (e.g. strip "$."). */
    formKeyFromAttrName: (name: string) => string;
    /** When true, view-only mode. */
    readOnly: boolean;
    /** Global loading state. */
    loading: boolean;
    /** Optional bindings for AllowedValues/RegEx (schema entity bindings). */
    schemaEntityBindings?: Record<string, { AllowedValues?: unknown[]; RegEx?: string }>;
}

/** Attribute shape used by the resolver. 8+ properties (open for label, type, required, etc.). */
export interface DynamicFieldAttribute {
    name?: string;
    id?: string;
    value?: string;
    label?: string;
    type?: string;
    required?: boolean;
    readOnly?: boolean;
    multiValued?: boolean;
    isReference?: boolean;
    isReferenceTo?: unknown[];
    [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Descriptor union – one variant per renderer type (static props only)
// ---------------------------------------------------------------------------

/** Base: every descriptor has a renderer discriminant. */
type DescriptorBase = { renderer: string };

/** SetTransition Direction: In/Out select. 4 properties. */
export interface SetTransitionDirectionDescriptor extends DescriptorBase {
    renderer: 'SetTransitionDirection';
    name: string;
    label: string;
    required: boolean;
}

/** Generic two-option select (e.g. Group Scope/Type). 6 properties. */
export interface GenericSelectDescriptor extends DescriptorBase {
    renderer: 'GenericSelect';
    name: string;
    label: string;
    required: boolean;
    options: { value: string; label: string }[];
}

/** Default text/string field. 4 properties. */
export interface DefaultTextFieldDescriptor extends DescriptorBase {
    renderer: 'DefaultText';
    name: string;
    label: string;
    required: boolean;
}

/** Single-valued reference (e.g. Manager). 5 properties. */
export interface ReferenceFieldDescriptor extends DescriptorBase {
    renderer: 'ReferenceField';
    name: string;
    label: string;
    required: boolean;
    entityTypes: string[];
}

/** Multi-valued reference (e.g. MemberOf). 5 properties. */
export interface ReferenceFieldMultiDescriptor extends DescriptorBase {
    renderer: 'ReferenceFieldMulti';
    name: string;
    label: string;
    required: boolean;
    entityTypes: string[];
}

/** Union of all descriptor variants. Add new renderer types here. */
export type DynamicFieldRendererDescriptor =
    | SetTransitionDirectionDescriptor
    | GenericSelectDescriptor
    | DefaultTextFieldDescriptor
    | ReferenceFieldDescriptor
    | ReferenceFieldMultiDescriptor;

// ---------------------------------------------------------------------------
// Runtime – base props supplied when rendering (value, onChange, disabled)
// ---------------------------------------------------------------------------

/** Base runtime props passed when rendering any field. 7 properties. */
export interface DynamicFieldRuntimeBase {
    /** Current form values. */
    formData: Record<string, unknown>;
    /** Update a field value. */
    onFieldChange: (fieldName: string, value: unknown) => void;
    formKeyFromAttrName: (name: string) => string;
    /** Entity type (e.g. User, Group) – used by resolver. */
    entityType: string | null | undefined;
    readOnly: boolean;
    loading: boolean;
}

/** Optional supplies for specific renderer types (e.g. SetSelector, workflow browser, reference fields). 10+ properties; extend as needed. */
export interface DynamicFieldSupplies {
    setActionWorkflowBrowserOpen?: (open: boolean) => void;
    selectedActionWorkflow?: { Id?: string; DisplayName?: string } | null;
    actionWorkflowError?: string | null;
    /** For ReferenceField: display text from cache or formData (uses getEntityValueFromARef internally). */
    getReferenceDisplayName?: (formData: Record<string, unknown>, fieldRef: string | EntityRef) => string;
    onReferenceEntityUpdate?: (fieldName: string, entity: EntityRef) => void;
    onReferenceEntityClear?: (fieldName: string) => void;
    entityService?: unknown;
    /** Which multi-ref field has the browser open; parent renders EntityBrowserDialog when set. */
    refMultiOpenField?: string | null;
    setRefMultiOpenField?: (name: string | null) => void;
    /** Optional: open a paginated viewer for the given reference-array field (read-only). */
    openRefMultiViewer?: (fieldName: string) => void;
}

/** Full runtime passed to renderDynamicField. 8 properties (base + supplies). */
export interface DynamicFieldRuntime extends DynamicFieldRuntimeBase {
    supplies?: DynamicFieldSupplies;
}

// ---------------------------------------------------------------------------
// Resolver – returns which renderer and static params (no values/callbacks)
// ---------------------------------------------------------------------------

/**
 * Returns the descriptor for which specialized renderer to use and its static props.
 * Pure: only depends on attr and entity type (and readOnly/loading for future use).
 */
export function getDynamicFieldRendererDescriptor(
    attr: DynamicFieldAttribute,
    input: GetDynamicFieldRendererDescriptorInput
): DynamicFieldRendererDescriptor {
    const name = (attr.name ?? attr.id ?? attr.value)?.toString().trim();
    if (!name) {
        return { renderer: 'DefaultText', name: '', label: '', required: false };
    }
    const label = (attr.label ?? name).toString();
    const required = attr.required === true;
    const entityType = input.entityType ?? '';
    const flags = getEntityTypeFlags(entityType);
    const attrRef = createEntityRef(name);

    if (flags.isSetTransition && isARef(attrRef, 'Direction')) {
        return { renderer: 'SetTransitionDirection', name, label, required };
    }

    if (entityType?.toLowerCase() === 'group' && isARef(attrRef, 'Scope')) {
        return {
            renderer: 'GenericSelect',
            name,
            label,
            required: true,
            options: [
                { value: 'Global', label: 'Global' },
                { value: 'Local', label: 'Local' },
                { value: 'Universal', label: 'Universal' },
            ],
        };
    }

    if (entityType?.toLowerCase() === 'group' && isARef(attrRef, 'Type')) {
        return {
            renderer: 'GenericSelect',
            name,
            label,
            required: true,
            options: [
                { value: 'Security', label: 'Security' },
                { value: 'Distribution', label: 'Distribution' },
            ],
        };
    }

    const refTo = (attr.isReferenceTo && Array.isArray(attr.isReferenceTo) && attr.isReferenceTo.length > 0
        ? attr.isReferenceTo
        : (attr.Entities ?? attr.entities ?? [])) as unknown[];
    const entityTypeNames = refTo.map((ent: unknown) =>
        typeof ent === 'string' ? ent : String((ent as { DisplayName?: string; Id?: string })?.DisplayName ?? (ent as { Id?: string })?.Id ?? ent)
    ).filter(Boolean) as string[];

    if (!attr.multiValued && attr.isReference && !flags.isSetTransition && entityType?.toLowerCase() !== 'policyrule' && entityTypeNames.length > 0) {
        return { renderer: 'ReferenceField', name, label, required, entityTypes: entityTypeNames };
    }

    if (attr.multiValued && attr.isReference && entityTypeNames.length > 0) {
        return { renderer: 'ReferenceFieldMulti', name, label, required, entityTypes: entityTypeNames };
    }

    return { renderer: 'DefaultText', name, label, required };
}

// ---------------------------------------------------------------------------
// DeferredLoadPlaceholder – "Not loaded*" + Load button (unindexed/sensitive attributes)
// ---------------------------------------------------------------------------

/** Props for DeferredLoadPlaceholder. */
export interface DeferredLoadPlaceholderProps {
    /** Field name (for key/aria). */
    fieldName: string;
    /** Label shown above the placeholder (e.g. "Display Name *"). */
    fieldLabel: React.ReactNode;
    /** When true, show loader on the Load button. */
    loading: boolean;
    /** Called when user clicks Load (e.g. fetch attribute value from API). */
    onLoad?: () => void | Promise<void>;
    /** When set, show "View" instead of "Load" and call this (e.g. open paginated entity viewer). Takes precedence over onLoad. */
    onView?: () => void;
    /** Optional tooltip for the "Not loaded*" text. */
    tooltipLabel?: string;
    /** When true, Load button is hidden (read-only view). */
    readOnly?: boolean;
    /** Tier3 size hint shown inline, e.g. "≈ 12 KB" (from Length/Count). */
    sizeEstimate?: string | null;
}

/**
 * Placeholder for deferred/unindexed attribute value: shows "Not loaded*" and a Load button.
 * Parent should call onRequestAttributeValue when onLoad fires and update form data.
 * When readOnly is true, only "Not loaded*" is shown (no Load button).
 */
export function DeferredLoadPlaceholder({
    fieldName,
    fieldLabel,
    loading,
    onLoad,
    onView,
    tooltipLabel = 'Sensitive, Large, or File value',
    readOnly = false,
    sizeEstimate = null,
}: DeferredLoadPlaceholderProps): React.ReactElement {
    const useView = !!onView;
    const handleAction = useView ? onView : onLoad;
    const buttonLabel = useView ? 'View' : 'Load';

    return (
        <Box key={fieldName} mb="md">
            <Text size="sm" fw={500} mb={4}>
                {fieldLabel}
            </Text>
            <Group gap="xs" align="center">
                <Tooltip label={tooltipLabel}>
                    <Text component="span" size="sm" c="dimmed">
                        Not loaded*{sizeEstimate ? ` (${sizeEstimate})` : ''}
                    </Text>
                </Tooltip>
                {!readOnly && handleAction && (
                    <Anchor
                        component="button"
                        type="button"
                        size="sm"
                        c="dimmed"
                        onClick={() => handleAction()}
                        style={{ cursor: loading && !useView ? 'wait' : 'pointer' }}
                    >
                        {loading && !useView ? (
                            <Group gap={4} align="center">
                                <Loader size={12} />
                                <span>{buttonLabel}</span>
                            </Group>
                        ) : (
                            buttonLabel
                        )}
                    </Anchor>
                )}
            </Group>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Required field label: asterisk with tooltip (shared so tests can assert on it)
// ---------------------------------------------------------------------------

/** Tooltip text for the required asterisk. Use in tests to query by title. */
export const REQUIRED_ASTERISK_TOOLTIP = 'Required';

/** Renders label plus optional required asterisk with tooltip. When readOnly, asterisk is omitted. */
export function FieldLabelWithRequired({
    label,
    required,
    readOnly = false,
}: { label: string; required: boolean; readOnly?: boolean }): React.ReactNode {
    if (!required || readOnly) return label;
    return (
        <>
            {label}{' '}
            <Tooltip label={REQUIRED_ASTERISK_TOOLTIP} withArrow position="top" openDelay={200}>
                <Box
                    component="span"
                    display="inline-block"
                    c="var(--mantine-color-red-6)"
                    style={{ cursor: 'help' }}
                    title={REQUIRED_ASTERISK_TOOLTIP}
                    aria-label={REQUIRED_ASTERISK_TOOLTIP}
                >
                    *
                </Box>
            </Tooltip>
        </>
    );
}

// ---------------------------------------------------------------------------
// Specialized renderer components – each has a small props interface
// ---------------------------------------------------------------------------

/** Props for SetTransition Direction field. 7 properties. */
export interface SetTransitionDirectionFieldProps extends SetTransitionDirectionDescriptor {
    value: unknown;
    onChange: (value: string) => void;
    disabled: boolean;
}

function SetTransitionDirectionField({
    name,
    label,
    required,
    value,
    onChange,
    disabled,
}: SetTransitionDirectionFieldProps): React.ReactElement {
    return (
        <Select
            key={name}
            label={<FieldLabelWithRequired label={label} required={required} readOnly={disabled} />}
            required={required}
            withAsterisk={false}
            value={String(value ?? '')}
            onChange={(v) => onChange(v ?? '')}
            disabled={disabled}
            data={[{ value: 'In', label: 'In' }, { value: 'Out', label: 'Out' }]}
            mb="md"
        />
    );
}

/** Props for GenericSelect field. 8 properties. */
export interface GenericSelectFieldProps extends GenericSelectDescriptor {
    value: unknown;
    onChange: (value: string) => void;
    disabled: boolean;
    /** Optional: restrict options (e.g. Group Scope: hide Local unless already set). */
    optionsFilter?: (opts: { value: string; label: string }[]) => { value: string; label: string }[];
}

function GenericSelectField({
    name,
    label,
    required,
    options,
    value,
    onChange,
    disabled,
    optionsFilter,
}: GenericSelectFieldProps): React.ReactElement {
    const data = optionsFilter ? optionsFilter(options) : options;
    return (
        <Select
            key={name}
            label={<FieldLabelWithRequired label={label} required={required} readOnly={disabled} />}
            required={required}
            withAsterisk={false}
            value={String(value ?? '')}
            onChange={(v) => onChange(v ?? '')}
            disabled={disabled}
            data={data}
            mb="md"
        />
    );
}

/** Props for default text field. 7 properties. */
export interface DefaultTextFieldProps extends DefaultTextFieldDescriptor {
    value: unknown;
    onChange: (value: string) => void;
    disabled: boolean;
}

function DefaultTextField({
    name,
    label,
    required,
    value,
    onChange,
    disabled,
}: DefaultTextFieldProps): React.ReactElement {
    const rawDisplay = value != null && value !== '' ? String(value) : '';
    const display = disabled ? formatPhoneDisplayIfApplicable(label, rawDisplay) : rawDisplay;
    return (
        <TextInput
            key={name}
            label={<FieldLabelWithRequired label={label} required={required} readOnly={disabled} />}
            value={display}
            onChange={(e) => onChange(e.currentTarget.value)}
            onBlur={(e) => {
                if (disabled) return;
                const trimmed = e.currentTarget.value.trim();
                if (e.currentTarget.value !== trimmed) onChange(trimmed);
            }}
            disabled={disabled}
            required={required}
            withAsterisk={false}
            mb="md"
        />
    );
}

/** Props for single reference field. 10+ properties (descriptor + value, onChange, disabled, supplies). */
export interface ReferenceFieldProps extends ReferenceFieldDescriptor {
    value: unknown;
    onChange: (value: string) => void;
    /** When provided, GenericReferenceField uses this (fieldName, value) => void so the ref is stored, not the field name. */
    onFieldChange?: (fieldName: string, value: string | EntityRef) => void;
    disabled: boolean;
    displayName: string;
    onReferenceEntityUpdate: (fieldName: string, entity: EntityRef) => void;
    onReferenceEntityClear: (fieldName: string) => void;
    entityService?: unknown;
}

function ReferenceField({
    name,
    label,
    required,
    entityTypes,
    value,
    onChange,
    onFieldChange,
    disabled,
    displayName,
    onReferenceEntityUpdate,
    onReferenceEntityClear,
    entityService,
}: ReferenceFieldProps): React.ReactElement {
    return (
        <Box key={name} mb="md">
            <GenericReferenceField
                name={name}
                label={label}
                value={value as string | EntityRef | null | undefined}
                isRequired={required}
                loading={disabled}
                entityTypes={entityTypes}
                displayName={displayName}
                onFieldChange={onFieldChange ?? ((_, v) => onChange(typeof v === 'string' ? v : v.id))}
                onReferenceEntityUpdate={onReferenceEntityUpdate}
                onReferenceEntityClear={onReferenceEntityClear}
                entityService={entityService}
            />
        </Box>
    );
}

/** Max refs to render as badges; above this we cap display and show a warning to avoid UI overload. */
const REFERENCE_FIELD_MULTI_DISPLAY_CAP = 30;

/** Props for multi-valued reference field. 9+ properties. */
export interface ReferenceFieldMultiProps extends ReferenceFieldMultiDescriptor {
    value: unknown;
    onFieldChange: (name: string, value: unknown) => void;
    disabled: boolean;
    setRefMultiOpenField: (name: string | null) => void;
    openRefMultiViewer?: (fieldName: string) => void;
}

function ReferenceFieldMulti({
    name,
    label,
    required,
    entityTypes,
    value,
    onFieldChange,
    disabled,
    setRefMultiOpenField,
    openRefMultiViewer,
}: ReferenceFieldMultiProps): React.ReactElement {
    const currentRefs = Array.isArray(value) ? value : (value ? [value] : []);
    const refItems: EntityRef[] = currentRefs
        .map((item: unknown) => tryCreateEntityRef(item))
        .filter((ref): ref is EntityRef => ref != null && ref.id.length > 0);

    const overCap = refItems.length > REFERENCE_FIELD_MULTI_DISPLAY_CAP;
    const refsToShow = overCap ? refItems.slice(0, REFERENCE_FIELD_MULTI_DISPLAY_CAP) : refItems;

    const removeRef = (idToRemove: string) => {
        const next = refItems
            .filter((r) => r.id !== idToRemove)
            .map((r) => ({
                Id: r.id,
                DisplayName: r.displayName || undefined,
                ...(r.entityType && { EntityType: r.entityType })
            }));
        onFieldChange(name, next);
    };

    const removeAll = () => {
        onFieldChange(name, []);
    };

    return (
        <Box key={name} mb="md">
            <Text component="label" size="sm" fw={required ? 500 : 400} mb="xs" display="block">
                <FieldLabelWithRequired label={label} required={required} readOnly={disabled} />
            </Text>
            {overCap && (
                <Alert variant="light" color="orange" mb="xs" title="Too many to show">
                    Showing first {REFERENCE_FIELD_MULTI_DISPLAY_CAP} of {refItems.length} members. Use Add or Remove all to manage the full list.
                </Alert>
            )}
            <Box
                id={`multi-ref-${name}`}
                component="div"
                onClick={
                    !disabled
                        ? () => setRefMultiOpenField(name)
                        : (openRefMultiViewer && refItems.length > 0 ? () => openRefMultiViewer(name) : undefined)
                }
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 8,
                    alignItems: 'center',
                    minHeight: 40,
                    padding: 8,
                    border: '1px solid var(--mantine-color-default-border)',
                    borderRadius: 4,
                    cursor: (!disabled || (openRefMultiViewer && refItems.length > 0)) ? 'pointer' : undefined,
                }}
            >
                {refItems.length === 0 && !disabled && (
                    <Text size="sm" c="dimmed">
                        Click to select {entityTypes.length > 1 ? '— choose entity type in dialog' : entityTypes[0]}
                    </Text>
                )}
                {refsToShow.map((ref) => (
                    <Group key={ref.id} gap={4} wrap="nowrap" onMouseDown={(e) => e.stopPropagation()} style={{ cursor: 'default' }}>
                        <MultiReferenceEntityBadge
                            entityId={ref.id}
                            displayName={ref.displayName}
                            entityType={ref.entityType}
                            fallbackEntityTypes={entityTypes}
                            leftClickNavigation={disabled ? 'sameTab' : 'newTab'}
                        />
                        {!disabled && (
                            <ActionIcon
                                variant="subtle"
                                size="xs"
                                onClick={(e) => { e.stopPropagation(); removeRef(ref.id); }}
                                aria-label="Remove"
                            >
                                <IconX size={12} />
                            </ActionIcon>
                        )}
                    </Group>
                ))}
                {overCap && (
                    <Text size="sm" c="dimmed" component="span">
                        … +{refItems.length - REFERENCE_FIELD_MULTI_DISPLAY_CAP} more
                    </Text>
                )}
                {!disabled && refItems.length > 0 && (
                    <>
                        <Button variant="outlined" size="xs" onClick={(e) => { e.stopPropagation(); setRefMultiOpenField(name); }}>
                            Add
                        </Button>
                        <Button variant="subtle" size="xs" color="red" onClick={(e) => { e.stopPropagation(); removeAll(); }} aria-label="Remove all">
                            Remove all
                        </Button>
                    </>
                )}
                {disabled && openRefMultiViewer && refItems.length > 0 && (
                    <Button
                        variant="subtle"
                        size="xs"
                        onClick={(e) => { e.stopPropagation(); openRefMultiViewer(name); }}
                    >
                        View
                    </Button>
                )}
            </Box>
            {entityTypes.length > 0 && (
                <Text size="xs" c="dimmed" display="block" mt="xs">
                    Select from: {entityTypes.join(', ')}
                </Text>
            )}
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Optional: debug wrapper (small interface – 4 properties)
// ---------------------------------------------------------------------------

/** Props for optional field debug tooltip. 4 properties. */
export interface FieldDebugWrapperProps {
    fieldName: string;
    content: React.ReactNode;
    showDebug: boolean;
    getDebugContent: (fieldName: string) => React.ReactNode;
}

function FieldDebugWrapper({ fieldName, content, showDebug, getDebugContent }: FieldDebugWrapperProps): React.ReactNode {
    if (!showDebug) return content;
    return (
        <Group key={fieldName} gap={4} align="flex-start">
            <Box flex={1} miw={0}>{content}</Box>
            <Tooltip label={getDebugContent(fieldName)} position="left" withArrow>
                <Box mt={6} display="inline-flex">
                    <IconInfoCircle size={18} />
                </Box>
            </Tooltip>
        </Group>
    );
}

// ---------------------------------------------------------------------------
// Main entry: render using descriptor + runtime
// ---------------------------------------------------------------------------

/**
 * Renders a single dynamic form field by resolving the renderer type, then delegating to the specialized control.
 * Runtime provides form values and callbacks; supplies (optional) provide renderer-specific deps.
 */
export function renderDynamicField(
    attr: DynamicFieldAttribute,
    runtime: DynamicFieldRuntime
): React.ReactNode {
    const name = (attr.name ?? attr.id ?? attr.value)?.toString().trim();
    if (!name) return null;

    const input: GetDynamicFieldRendererDescriptorInput = {
        attr,
        entityType: runtime.entityType,
        formKeyFromAttrName: runtime.formKeyFromAttrName,
        readOnly: runtime.readOnly,
        loading: runtime.loading,
        schemaEntityBindings: undefined,
    };
    const descriptor = getDynamicFieldRendererDescriptor(attr, input);
    const key: string = descriptor.name || name;
    const formKey = runtime.formKeyFromAttrName(key);
    const value = runtime.formData[formKey] ?? runtime.formData[key] ?? '';
    const disabled = runtime.loading || runtime.readOnly || (attr.readOnly === true);

    const onChange = (v: string) => runtime.onFieldChange(key, v);
    const supplies = runtime.supplies;

    let content: React.ReactNode;
    switch (descriptor.renderer) {
        case 'SetTransitionDirection':
            content = (
                <SetTransitionDirectionField
                    {...descriptor}
                    value={value}
                    onChange={onChange}
                    disabled={disabled}
                />
            );
            break;
        case 'GenericSelect': {
            const displayValue = descriptor.name === 'Scope' && !value ? 'Universal' : value;
            const optionsFilter =
                descriptor.name === 'Scope'
                    ? (opts: { value: string; label: string }[]) =>
                          opts.filter((o) => o.value !== 'Local' || displayValue === 'Local')
                    : undefined;
            content = (
                <GenericSelectField
                    {...descriptor}
                    value={displayValue}
                    onChange={onChange}
                    disabled={disabled}
                    optionsFilter={optionsFilter}
                />
            );
            break;
        }
        case 'ReferenceField': {
            const displayName = supplies?.getReferenceDisplayName ? supplies.getReferenceDisplayName(runtime.formData ?? {}, key) : String(value ?? '');
            const onRefUpdate = supplies?.onReferenceEntityUpdate ?? (() => {});
            const onRefClear = supplies?.onReferenceEntityClear ?? (() => {});
            // GenericReferenceField calls onFieldChange(fieldName, value); we must store the second arg (the ref), not the first (field name)
            const onFieldChangeRef = (_fieldName: string, fieldValue: string | EntityRef) => runtime.onFieldChange(key, fieldValue);
            if (supplies?.getReferenceDisplayName && supplies?.onReferenceEntityUpdate && supplies?.onReferenceEntityClear) {
                content = (
                    <ReferenceField
                        {...descriptor}
                        value={value}
                        onChange={onChange}
                        onFieldChange={onFieldChangeRef}
                        disabled={disabled}
                        displayName={displayName}
                        onReferenceEntityUpdate={onRefUpdate}
                        onReferenceEntityClear={onRefClear}
                        entityService={supplies?.entityService}
                    />
                );
            } else {
                const fallbackDesc: DefaultTextFieldDescriptor = { renderer: 'DefaultText', name: descriptor.name, label: descriptor.label, required: descriptor.required };
                content = (
                    <DefaultTextField
                        {...fallbackDesc}
                        value={value}
                        onChange={onChange}
                        disabled={disabled}
                    />
                );
            }
            break;
        }
        case 'ReferenceFieldMulti': {
            if (supplies?.setRefMultiOpenField != null) {
                const onFieldChangeMulti = (fieldName: string, v: unknown) => runtime.onFieldChange(fieldName, v);
                content = (
                    <ReferenceFieldMulti
                        {...descriptor}
                        value={value}
                        onFieldChange={onFieldChangeMulti}
                        disabled={disabled}
                        setRefMultiOpenField={supplies.setRefMultiOpenField}
                        openRefMultiViewer={supplies.openRefMultiViewer}
                    />
                );
            } else {
                const fallbackDesc: DefaultTextFieldDescriptor = { renderer: 'DefaultText', name: descriptor.name, label: descriptor.label, required: descriptor.required };
                content = (
                    <DefaultTextField
                        {...fallbackDesc}
                        value={value}
                        onChange={onChange}
                        disabled={disabled}
                    />
                );
            }
            break;
        }
        case 'DefaultText':
        default:
            content = (
                <DefaultTextField
                    {...descriptor}
                    value={value}
                    onChange={onChange}
                    disabled={disabled}
                />
            );
    }

    return (
        <Box component="span" style={{ display: 'contents' }} data-renderer={descriptor.renderer} data-field-name={key}>
            {content}
        </Box>
    );
}
